import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { NOTIFICATIONS_STORE } from '@shared/definitions/stores/notifications-store.token';
import { InvitationNotificationService } from '@core/services/auth/invitation-notification.service';
import { NotificationListComponent } from '../notification-list/notification-list.component';
import { PushOptInBannerComponent } from '../push-opt-in-banner/push-opt-in-banner.component';
import {
  Notification,
  NotificationSummary
} from '@shared/definitions/notification.interface';

@Component({
    selector: 'app-notification-bell',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatBadgeModule,
        MatMenuModule,
        MatTooltipModule,
        MatDividerModule,
        TranslatePipe,
        NotificationListComponent,
        PushOptInBannerComponent
    ],
    templateUrl: './notification-bell.component.html',
    styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent {

  summary$: Observable<NotificationSummary | null>;
  urgentNotifications$: Observable<Notification[]>;
  combinedCount$: Observable<number>;
  readonly invitationCount = signal(0);

  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsStore = inject(NOTIFICATIONS_STORE);

  constructor(
    private invitationService: InvitationNotificationService,
    private router: Router
  ) {
    this.summary$ = toObservable(this.notificationsStore.summary);
    this.urgentNotifications$ = toObservable(
      computed(() => [...this.notificationsStore.urgent()] as Notification[])
    );

    // Combine notification count with invitation count; debounce to batch
    // rapid co-emissions from upstream stores.
    this.combinedCount$ = combineLatest([
      this.summary$,
      this.invitationService.pendingInvitations$
    ]).pipe(
      debounceTime(100),
      map(([summary, invitations]) => (summary?.unreadCount || 0) + invitations.length),
      distinctUntilChanged()
    );

    this.invitationService.pendingInvitations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(invitations => this.invitationCount.set(invitations.length));
  }

  /**
   * Navigate to full notifications page
   */
  viewAllNotifications(): void {
    void this.router.navigate(['/apps/notifications']);
  }

  /**
   * Handle notification click from dropdown
   */
  onNotificationClick(notification: Notification): void {
    // The notification list component handles marking as read and navigation
    // Close the menu by navigating or taking action
    if (notification.actionUrl) {
      void this.router.navigateByUrl(notification.actionUrl);
    }
  }

  /**
   * Get badge content for unread notifications + invitations
   */
  getBadgeContent(summary: NotificationSummary | null): string {
    const notificationCount = summary?.unreadCount || 0;
    const totalCount = notificationCount + this.invitationCount();

    if (totalCount === 0) {
      return '';
    }

    return totalCount > 99 ? '99+' : totalCount.toString();
  }

  /**
   * Get tooltip text for notification bell
   */
  getTooltipText(summary: NotificationSummary | null): string {
    const notificationCount = summary?.unreadCount || 0;
    const invitations = this.invitationCount();
    const totalCount = notificationCount + invitations;

    if (totalCount === 0) {
      return 'No new notifications';
    }

    const parts: string[] = [];
    if (notificationCount > 0) {
      parts.push(`${notificationCount} notification${notificationCount === 1 ? '' : 's'}`);
    }
    if (invitations > 0) {
      parts.push(`${invitations} invitation${invitations === 1 ? '' : 's'}`);
    }

    return parts.join(' · ');
  }

  /**
   * Check if there are urgent notifications
   */
  hasUrgentNotifications(notifications: Notification[] | null): boolean {
    return notifications ? notifications.length > 0 : false;
  }
} 